import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getPaymentStatus } from "../api";

// Poll settings: 2s interval, 60s max (~30 attempts) before we give up
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 60_000;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [orderNumber, setOrderNumber] = useState(null);
  const { clear } = useCart();
  const nav = useNavigate();
  const startedAt = useRef(Date.now());
  const cartCleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await getPaymentStatus(sessionId);
        if (cancelled) return;
        setOrderNumber(r.order_number || null);
        if (r.payment_status === "paid") {
          if (!cartCleared.current) {
            clear();
            cartCleared.current = true;
            try { localStorage.removeItem("pending_order"); } catch {}
          }
          setState("paid");
          // Auto-redirect to the order confirmation after a short pause
          if (r.order_number) {
            setTimeout(() => nav(`/order/${r.order_number}`), 1500);
          }
          return;
        }
        if (Date.now() - startedAt.current > POLL_MAX_MS) {
          setState("timeout");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (Date.now() - startedAt.current > POLL_MAX_MS) {
          setState("error");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();
    return () => { cancelled = true; };
  }, [sessionId, clear, nav]);

  return (
    <main className="min-h-screen bg-black text-white pt-32 pb-24">
      <div className="max-w-xl mx-auto px-6 text-center" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-purple-300 mx-auto animate-spin" />
            <h1 className="mt-6 font-headline text-4xl font-bold text-white">
              Confirming your payment…
            </h1>
            <p className="mt-3 text-white/60">
              We're checking with Stripe. This normally takes a couple of seconds.
              Don't close this tab.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <h1 className="mt-6 font-headline text-4xl md:text-5xl font-bold text-gradient-purple">
              Payment received.
            </h1>
            <p className="mt-4 text-white/70">
              Thanks — your card was charged successfully.
              {orderNumber && (<> Order reference <span className="font-mono text-purple-300">{orderNumber}</span>.</>)}
            </p>
            <p className="mt-2 text-sm text-white/50">Redirecting you to your order confirmation…</p>
            {orderNumber && (
              <Link
                to={`/order/${orderNumber}`}
                className="btn-primary mt-8 inline-flex"
                data-testid="view-order-btn"
              >
                View order <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </>
        )}

        {state === "timeout" && (
          <>
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
            <h1 className="mt-6 font-headline text-3xl font-bold text-white">
              Still waiting on Stripe.
            </h1>
            <p className="mt-3 text-white/60">
              Your card may still complete in the background. If you don't see
              a confirmation email in a few minutes, contact us with your session
              reference below.
            </p>
            <div className="mt-4 font-mono text-xs text-white/50 break-all">{sessionId}</div>
            <Link to="/lookup" className="btn-primary mt-8 inline-flex">
              Look up an order <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <AlertTriangle className="h-10 w-10 text-red-400 mx-auto" />
            <h1 className="mt-6 font-headline text-3xl font-bold text-white">
              We couldn't confirm this session.
            </h1>
            <p className="mt-3 text-white/60">
              If you did pay, your order will still be captured via our webhook
              within a minute. Try refreshing, or look up your order by reference.
            </p>
            <Link to="/lookup" className="btn-primary mt-8 inline-flex">
              Look up an order <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
